import { createTransactionObserver, type DbExecutor } from "./executor";

/**
 * HOSTED PERSISTENCE (Phase 13A, spec §10 and §57).
 *
 * The same repositories, services and engines run unchanged in a browser;
 * only the persistence boundary moves. This executor sends each statement to
 * the tester's own server-side SQLite workspace.
 *
 * TRANSACTIONS are a server-side session, exactly as they are a Rust command
 * on the desktop: a browser cannot hold BEGIN…COMMIT open by itself. Nested
 * calls join the outer transaction (the reentrancy contract in executor.ts),
 * and every statement inside one carries its id so the server can refuse a
 * stray write.
 */
export interface HttpExecutorOptions {
  /** Called when the server says the session has ended. */
  onUnauthorized?: () => void;
  baseUrl?: string;
}

export class HttpDbError extends Error {
  /** The server text behind a friendly message, for diagnostics only. */
  detail?: string;
}

/**
 * Spec §87.22: a person never reads SQLite. The server reports a failed
 * statement in the database's own words ("UNIQUE constraint failed: …");
 * those are replaced here with a sentence that says what to do, and the
 * original is kept on the error for the diagnostic record. Messages the
 * server wrote for people pass through untouched.
 */
export function friendlyDbMessage(raw: string): string {
  return /sqlite|constraint|no such (table|column)|syntax error|datatype mismatch|malformed|disk i\/o|database is locked|near "/i.test(raw)
    ? "Berrier Reno could not save that change. Nothing else was affected — reload the page and try again, and send feedback if it keeps happening."
    : raw;
}

/**
 * SHARED READS (spec §87.23, measured). Opening a project tab mounts several
 * panels that each load the same financial truth — the same expenses,
 * purchases, mileage and estimate lines — so one Scope & Estimate visit cost
 * ~526 network round trips, the same handful of SELECTs issued 15–33 times.
 * An identical SELECT (same text, same parameters, outside a transaction) that
 * is still in flight, or finished within READ_REUSE_MS, is answered with that
 * same result instead of another round trip.
 *
 * Nothing stale is ever served past a write: every execute, every
 * transaction boundary, a server-side estimate regeneration and every live
 * update from a collaborator bump the generation and empty the cache.
 */
const READ_REUSE_MS = 400;
const invalidators = new Set<() => void>();

/** Forget every shared read (a write happened somewhere this client cannot see). */
export function invalidateHttpReads(): void {
  for (const invalidate of invalidators) invalidate();
}

export function createHttpExecutor(options: HttpExecutorOptions = {}): DbExecutor {
  const observer = createTransactionObserver();
  const base = options.baseUrl ?? "";
  let transactionId: string | null = null;
  let depth = 0;
  // Bodies that joined the open transaction. A browser has ONE connection and
  // no way to tell a nested call from a concurrent one, so a call that
  // arrives while a transaction is open joins it (the reentrancy contract) —
  // and the transaction must then wait for that work before committing.
  // Otherwise the joiner's remaining statements land after COMMIT, which the
  // server refuses, and an edit is silently lost while the screen says Saved.
  let joined: Array<Promise<unknown>> = [];
  let generation = 0;
  const reads = new Map<string, { generation: number; settledAt: number | null; rows: Promise<unknown[]> }>();
  const invalidate = () => {
    generation += 1;
    reads.clear();
  };
  invalidators.add(invalidate);

  async function post<T>(path: string, body: unknown): Promise<T> {
    let response: Response;
    try {
      response = await fetch(`${base}${path}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "same-origin",
        body: JSON.stringify(body),
      });
    } catch {
      // The connection dropped, the server is restarting, or the machine slept.
      throw new HttpDbError("Berrier Reno cannot reach its server. Check your internet connection, then reload this page.");
    }
    if (response.status === 401) {
      options.onUnauthorized?.();
      throw new HttpDbError("Your session has ended. Sign in again to continue.");
    }
    const payload = (await response.json().catch(() => ({}))) as { error?: string } & T;
    if (!response.ok) {
      const raw = payload.error ?? "The server could not complete that request.";
      const error = new HttpDbError(friendlyDbMessage(raw));
      error.detail = raw;
      throw error;
    }
    return payload;
  }

  return {
    whenIdle: observer.whenIdle,
    async select<T>(sql: string, params: unknown[] = []): Promise<T[]> {
      if (transactionId !== null) {
        const result = await post<{ rows: T[] }>("/api/db/select", { sql, params, transactionId });
        return result.rows;
      }
      const key = `${sql}\u0000${JSON.stringify(params)}`;
      const cached = reads.get(key);
      const now = Date.now();
      if (cached && cached.generation === generation && (cached.settledAt === null || now - cached.settledAt < READ_REUSE_MS)) {
        return ((await cached.rows) as T[]).slice();
      }
      const issuedAt = generation;
      const entry = { generation: issuedAt, settledAt: null as number | null, rows: Promise.resolve([] as unknown[]) };
      entry.rows = post<{ rows: T[] }>("/api/db/select", { sql, params, transactionId: null }).then(
        (result) => {
          entry.settledAt = Date.now();
          return result.rows as unknown[];
        },
        (err: unknown) => {
          if (reads.get(key) === entry) reads.delete(key);
          throw err;
        },
      );
      reads.set(key, entry);
      return ((await entry.rows) as T[]).slice();
    },

    async execute(sql: string, params: unknown[] = []): Promise<void> {
      invalidate();
      try {
        await post("/api/db/execute", { sql, params, transactionId });
      } finally {
        invalidate();
      }
    },

    async transaction<T>(fn: () => Promise<T>): Promise<T> {
      if (depth > 0) {
        depth += 1;
        const running = (async () => {
          try {
            return await fn();
          } finally {
            depth -= 1;
          }
        })();
        joined.push(running);
        return await running;
      }
      invalidate();
      const settled = observer.begin();
      let begun: { transactionId: string };
      try {
        begun = await post<{ transactionId: string }>("/api/db/tx/begin", {});
      } catch (error) {
        settled();
        throw error;
      }
      const id = begun.transactionId;
      transactionId = id;
      depth = 1;
      try {
        const result = await fn();
        // Everything that joined this transaction finishes INSIDE it.
        while (joined.length > 0) {
          const outstanding = joined;
          joined = [];
          await Promise.allSettled(outstanding);
        }
        // The body is done, so the transaction is no longer AMBIENT: a panel
        // that refreshes while the commit is in flight must not stamp its
        // read with a transaction that is closing. The commit and rollback
        // carry the id explicitly, exactly as the desktop clears its own
        // in-transaction flag before invoking plan_tx_commit.
        transactionId = null;
        depth = 0;
        await post("/api/db/tx/commit", { transactionId: id });
        return result;
      } catch (err) {
        transactionId = null;
        depth = 0;
        joined = [];
        try {
          await post("/api/db/tx/rollback", { transactionId: id });
        } catch {
          /* the original failure is the one that matters */
        }
        throw err;
      } finally {
        transactionId = null;
        depth = 0;
        joined = [];
        invalidate();
        settled();
      }
    },

    async close(): Promise<void> {
      /* The workspace stays open on the server; the session controls its life. */
    },
  };
}
